import { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Card } from "primereact/card";
import { handlePdfDia, imprimirPdfDia, handleExcelDia } from "../Impressoes/GerarPdfDia";
import { useReactToPrint } from "react-to-print";
import html2pdf from "html2pdf.js";
import * as XLSX from "xlsx";

export const DefeitoMetragemRevisado = ({ data, totalGeral, totalRevisado, periodo }) => {
    const ref = useRef();
    const [printing, setPrinting] = useState(false);
    const [expandedRows, setExpandedRows] = useState(null);

    const handlePrint = useReactToPrint({
        content: () => ref.current,
        onBeforeGetContent: () => {
            setPrinting(true);
            return new Promise((resolve) => {
                setTimeout(() => resolve(), 500);
            });
        },
        onAfterPrint: () => {
            setPrinting(false);
        }
    });

    const handlePdf = () => {
        const element = ref.current;

        const opt = {
            margin: 12,
            filename: `Relatorio_Acumulado_${periodo || ''}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        setPrinting(true);

        html2pdf().set(opt).from(element).save().then(() => {
            setPrinting(false);
        });
    };

    const handleExcel = () => {
        const linhasExportacao = [];

        data.forEach((grupo) => {
            grupo.dias.forEach((dia) => {
                const dataFormatada = formatDate(dia.data);
                const totalRevisadoDia = parseFloat(dia.total_revisado) || 0;
                const totalDefeitosDia = parseFloat(dia.metragem_defeito) || 0;
                const percentualDia = totalRevisadoDia > 0 ? (totalDefeitosDia / totalRevisadoDia) : 0;

                if (dia.defeitos && dia.defeitos.length > 0) {
                    dia.defeitos.forEach((def) => {
                        const metragemDef = parseFloat(def.metragem) || 0;
                        const percentualDef = totalRevisadoDia > 0 ? (metragemDef / totalRevisadoDia) : 0;

                        linhasExportacao.push({
                            "Setor": grupo.setor,
                            "Meta de Qualidade (%)": parseFloat(grupo.meta_qualidade), 
                            "Data": dataFormatada,
                            "Total Revisado (m)": totalRevisadoDia,
                            "Defeito do Dia (m)": totalDefeitosDia,
                            "% do Dia": (percentualDia * 100).toFixed(2),
                            "Nome do Defeito": def.defeito,
                            "Metragem Defeito (m)": metragemDef,
                            "% do Defeito": (percentualDef * 100).toFixed(2)
                        });
                    });
                } else {
                    linhasExportacao.push({
                        "Setor": grupo.setor,
                        "Meta de Qualidade (%)": parseFloat(grupo.meta_qualidade),
                        "Data": dataFormatada,
                        "Total Revisado (m)": totalRevisadoDia,
                        "Defeito do Dia (m)": totalDefeitosDia,
                        "% do Dia": (percentualDia * 100).toFixed(2),
                        "Nome do Defeito": "Nenhum",
                        "Metragem Defeito (m)": 0,
                        "% do Defeito": 0
                    });
                }
            });
        });

        const worksheet = XLSX.utils.json_to_sheet(linhasExportacao);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Detalhamento de Defeitos");

        XLSX.writeFile(workbook, `Relatorio_Defeitos_${periodo || 'Geral'}.xlsx`);
    };

    const formatNumber = (num) => {
        if (num === null || num === undefined) return "0,00";
        return parseFloat(num).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dataStr) => {
        if (!dataStr) return '';
        const apenasData = dataStr.split(' ')[0];
        const partes = apenasData.split('-');
        if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
        return dataStr;
    };

    const percentualBodyTemplate = (rowData, grupo) => {
        const data = rowData.percentual ? rowData.percentual : rowData;
        const isHigh = parseFloat(data) > parseFloat(grupo.meta_qualidade);

        return (
            <span style={{
                backgroundColor: isHigh ? '#fee2e2' : '#f8fafc', 
                color: isHigh ? '#dc2626' : 'inherit',                
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: isHigh ? 'bold' : 'normal'
            }}>
                {formatNumber(data)}%
            </span>
        );
    };

    if (!data || data.length === 0) return null;

    const metaGeral = (dados) => {
        return dados.reduce((total, item) => {
            return total + parseFloat(item.meta_qualidade);
        }, 0);
    };
    const totalMeta = metaGeral(data);
    const percentualGeral = totalRevisado > 0 ? (totalGeral / totalRevisado) * 100 : 0;
    const isHighGeral = percentualGeral > totalMeta; 

    const rowExpansionTemplate = (dia, grupo) => {
        const totalRevisado = parseFloat(dia.total_revisado) || 0;

        return (
            <div style={{ padding: '15px 40px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '15px' }}>
                    Defeitos do dia {formatDate(dia.data)}
                </h5>

                <div style={{ display: "flex", gap: "8px", marginBottom: "15px" }}>
                    <Button
                        icon="pi pi-print"
                        className="p-button-primary p-button-sm"
                        title="Imprimir"
                        onClick={() => imprimirPdfDia(dia, grupo)}
                    />

                    <Button
                        icon="pi pi-file-pdf"
                        className="p-button-danger p-button-sm"
                        title="Download de PDF"
                        onClick={() => handlePdfDia(dia, grupo)}
                    />

                    <Button
                        icon="pi pi-file-excel"
                        className="p-button-success p-button-sm"
                        title="Exportar para Excel"
                        onClick={() => handleExcelDia(dia, grupo)} 
                    />
                </div>

                <DataTable value={dia.defeitos || []} size="small" showGridlines>
                    <Column field="defeito" header="Defeito" />

                    <Column field="metragem" header="Metragem" 
                        body={(rowData) => (
                            `${formatNumber(rowData.metragem)} m`
                        )}
                    />

                    <Column header="%"
                        body={(rowData) => {
                            const metragem = parseFloat(rowData.metragem) || 0;
                            const percentual = totalRevisado > 0 ? (metragem / totalRevisado) * 100 : 0;
                            return `${formatNumber(percentual)}%`;
                        }}
                    />
                </DataTable>
            </div>
        );
    };


    return (
        <div>
            <Card>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                    {printing ? (
                        <i className="pi pi-spin pi-spinner"></i>
                    ) : (
                        <>
                            <Button 
                                className="p-button-primary"
                                icon="pi pi-print"
                                onClick={handlePrint}
                                title="Imprimir"
                            />
                            <Button 
                                className="p-button-danger"
                                icon="pi pi-file-pdf"
                                onClick={handlePdf}
                                disabled={printing}
                                title="Download de PDF"
                            />
                            <Button 
                                className="p-button-success"
                                icon="pi pi-file-excel"
                                onClick={handleExcel}
                                disabled={printing}
                                title="Exportar para Excel"
                            />
                        </>
                    )}
                </div>

                <div ref={ref}>
                    <div style={{ width: printing ? '90%' : '70%', marginLeft: printing ? '5%' : '15%', marginRight: printing ? '5%' : '15%'}}>
                        <h2 style={{ marginBottom: '2rem' }}>Relatório Acumulado de Defeitos por Setor - {periodo}</h2>

                        {data.map((grupo, index) => (
                            <div key={index} style={{ marginBottom: '2.5rem', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.05)', border: '1px solid #e0e0e0'}}>
                                
                                <h4 style={{ fontSize: '18px',margin: 0, padding: '12px', borderBottom: '2px solid #2196F3', display: 'flex',  justifyContent: 'space-between', alignItems: 'center'}}>
                                    {grupo.setor}
                                </h4>

                                <div style={{ margin: printing ? '25px' : '20px 50px' }}>
                                    <DataTable
                                        responsiveLayout="scroll"
                                        style={{ fontSize: 15 }}
                                        size="small"
                                        value={grupo.dias.map((dia) => ({
                                            ...dia,
                                            expansionKey: `${grupo.setor}-${dia.data}`
                                        }))}
                                        rowHover={true}
                                        expandedRows={expandedRows}
                                        onRowToggle={(e) => setExpandedRows(e.data)}
                                        rowExpansionTemplate={(dia) => rowExpansionTemplate(dia, grupo)}
                                        dataKey="expansionKey"
                                    >
                                        <Column expander style={{ width: '3rem' }}/>
                                        <Column header="Data" field="data" body={(rowData) => formatDate(rowData.data)} style={{ width: '120px' }} sortable/>
                                        <Column header="Metragem Defeito" field="metragem_defeito" body={(rowData) => formatNumber(rowData.metragem_defeito)} style={{ width: '120px' }} sortable/>
                                        <Column header="Total Revisado" field="total_revisado" body={(rowData) => formatNumber(rowData.total_revisado)} style={{ width: '120px' }} sortable/>
                                        <Column
                                            header="Percentual"
                                            body={(rowData) => percentualBodyTemplate(rowData, grupo)}
                                            style={{ width: '120px' }}
                                            sortable
                                        />
                                    </DataTable>

                                    <div className="p-p-2" style={{ fontWeight: 'bold', fontSize: '18px', display: 'flex', justifyContent: 'space-between', marginTop: '10px'}}>
                                        <span>Total: {formatNumber(grupo.total_defeito)} m</span>
                                        <span>Revisado: {formatNumber(grupo.total_revisado)} m</span>
                                        <span>Percentual: {percentualBodyTemplate(grupo.percentual_total, grupo, true)}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={{ width: printing ? '' : '72%', marginLeft: printing ? '' : '14%', marginRight: printing ? '' : '14%'}}>
                        <div style={{ backgroundColor: '#f8fafc', borderRadius: '8px', padding: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '2px solid #e2e8f0' }}>
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                Total Geral
                            </span>
                            
                            <div style={{ display: 'flex', gap: '30px', fontSize: '16px', fontWeight: 'bold', color: '#475569', alignItems: 'center' }}>
                                <span>Defeitos: <span style={{ color: '#0c6ab6' }}>{formatNumber(totalGeral)} m</span></span>
                                <span>Revisado: <span style={{ color: '#0c6ab6' }}>{formatNumber(totalRevisado)} m</span></span>
                                
                                <span style={{
                                    backgroundColor: isHighGeral ? '#fee2e2' : 'transparent',
                                    color: isHighGeral ? '#dc2626' : '#475569',
                                    padding: '6px',
                                    borderRadius: '6px',
                                    border: isHighGeral ? 'none' : '1px solid #cbd5e1'
                                }}>
                                    Percentual: <span style={{ padding: '2px', color: isHighGeral ? '' : '#0c6ab6' }}>{formatNumber(percentualGeral)}%</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}